import { useCallback, useEffect, useState } from "react";
import { theme, card } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { PortalLink } from "./PortalLink";

// The vulnerability exposure strip at the top of Threat Intelligence: what the evidence
// says right now (GET /threat-feeds/exposure), the sources it comes from, the automatic
// register entry switch (PATCH /threat-feeds/exposure/settings, risks:write) and a
// "Re-evaluate now" button (POST /threat-feeds/exposure/recompute, compliance:run).
// Contract: backend services/advisoryExposure.ts; docs/OPERATIONAL-RESILIENCE.md.

export interface ExposureStatus {
  lastRunAt: string | null;
  autoRiskEnabled: boolean;
  open: { confirmed: number; likely: number; advisories: number; assets: number; importantAssets: number };
  risksOpen: number;
  sources: {
    nvd: { enriched: number; pending: number; total: number; apiKey: boolean };
    intuneSoftware: { connected: boolean; lastSync: string | null; error: string | null; assetsWithSoftware: number };
    defender: { connected: boolean; enabled: boolean; lastSync: string | null; error: string | null; machines: number; assetsWithVulnerabilities: number; requiredPermissions: string[] };
  };
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "never";
}

function Stat({ value, label, tone }: { value: number; label: string; tone?: "danger" | "warning" | "muted" }) {
  const color = tone === "danger" ? theme.color.dangerText : tone === "warning" ? theme.color.warningText : theme.color.heading;
  return (
    <div style={{ display: "grid", gap: 1, minWidth: 88 }}>
      <span style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.1 }}>{value.toLocaleString()}</span>
      <span style={{ fontSize: 11, color: theme.color.muted }}>{label}</span>
    </div>
  );
}

function SourceLine({ ok, warn, text, detail }: { ok: boolean; warn?: boolean; text: string; detail?: string | null }) {
  const color = ok ? theme.color.successText : warn ? theme.color.warningText : theme.color.subtle;
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 11, lineHeight: 1.45 }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, marginTop: 5, flexShrink: 0 }} />
      <span style={{ color: theme.color.muted }}>{text}{detail && <span style={{ display: "block", color: theme.color.warningText }}>{detail}</span>}</span>
    </div>
  );
}

export function AdvisoryExposureStrip({ canToggleRisk, canRecompute, affecting, onAffectingChange, onRecomputed }: {
  canToggleRisk: boolean;
  canRecompute: boolean;
  affecting: "" | "any" | "confirmed";
  onAffectingChange: (v: "" | "any" | "confirmed") => void;
  onRecomputed?: () => void;
}) {
  const [status, setStatus] = useState<ExposureStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"toggle" | "recompute" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => api.get<ExposureStatus>("/threat-feeds/exposure").then(setStatus).catch((e) => setError(apiErrorMessage(e, "Could not load the exposure status"))), []);
  useEffect(() => { load(); }, [load]);

  async function toggleAutoRisk(next: boolean) {
    setBusy("toggle"); setNotice(null);
    try {
      await api.patch("/threat-feeds/exposure/settings", { autoRiskEnabled: next });
      setStatus((s) => (s ? { ...s, autoRiskEnabled: next } : s));
    } catch (e) { setNotice(apiErrorMessage(e, "Could not change the setting")); }
    finally { setBusy(null); }
  }

  async function recompute() {
    setBusy("recompute"); setNotice(null);
    try {
      const res = await api.post<{ summary: { exposures: { open: number; confirmed: number; created: number; resolved: number }; risks: { raised: number; remediated: number }; notified: number } | null }>("/threat-feeds/exposure/recompute", {});
      const s = res.summary;
      setNotice(s ? `Re-evaluated: ${s.exposures.open} open exposure${s.exposures.open === 1 ? "" : "s"} (${s.exposures.confirmed} confirmed), ${s.exposures.created} new, ${s.exposures.resolved} resolved; ${s.risks.raised} register entr${s.risks.raised === 1 ? "y" : "ies"} raised, ${s.risks.remediated} tentatively remediated.` : "Re-evaluated.");
      await load();
      onRecomputed?.();
    } catch (e) { setNotice(apiErrorMessage(e, "Could not re-evaluate")); }
    finally { setBusy(null); }
  }

  if (error) return <div style={{ ...card, padding: "12px 18px", marginBottom: 16, fontSize: 12, color: theme.color.dangerText }}>{error}</div>;
  if (!status) return null;
  const { open, sources } = status;
  const d = sources.defender;
  const chip = (label: string, value: "" | "any" | "confirmed", tone: "danger" | "warning" | "accent") => {
    const active = affecting === value;
    const colour = tone === "danger" ? theme.color.dangerText : tone === "warning" ? theme.color.warningText : theme.color.accent;
    return (
      <button type="button" onClick={() => onAffectingChange(active ? "" : value)} style={{ fontSize: 12, fontWeight: 500, padding: "5px 12px", borderRadius: 3, cursor: "pointer", border: `1px solid ${active ? colour : theme.color.border}`, background: active ? (tone === "danger" ? theme.color.dangerBg : tone === "warning" ? theme.color.warningBg : theme.color.accentBg) : "transparent", color: active ? colour : theme.color.muted }}>
        {label}
      </button>
    );
  };

  return (
    <div style={{ ...card, padding: "14px 18px", marginBottom: 16, display: "grid", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.06em" }}>Vulnerability exposure</p>
        <span style={{ fontSize: 11, color: theme.color.faint }}>Last evaluated {when(status.lastRunAt)}</span>
        {canRecompute && (
          <button type="button" disabled={busy !== null} onClick={recompute} style={{ marginLeft: "auto", fontSize: 11, padding: "4px 10px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.muted, cursor: "pointer" }}>
            {busy === "recompute" ? "Re-evaluating…" : "Re-evaluate now"}
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Stat value={open.confirmed} label="confirmed exposures" tone={open.confirmed ? "danger" : "muted"} />
        <Stat value={open.likely} label="likely exposures" tone={open.likely ? "warning" : "muted"} />
        <Stat value={open.advisories} label="advisories reaching assets" />
        <Stat value={open.assets} label={`assets affected${open.importantAssets ? ` (${open.importantAssets} important)` : ""}`} tone={open.importantAssets ? "warning" : undefined} />
        <Stat value={status.risksOpen} label="automatic register entries open" />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginLeft: "auto" }}>
          {chip("Affecting my assets", "any", "warning")}
          {chip("Confirmed only", "confirmed", "danger")}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 8, paddingTop: 8, borderTop: `1px solid ${theme.color.border}` }}>
        <SourceLine ok={sources.nvd.enriched > 0} text={`NVD: ${sources.nvd.enriched.toLocaleString()} CVE${sources.nvd.enriched === 1 ? "" : "s"} described${sources.nvd.pending ? `, ${sources.nvd.pending.toLocaleString()} awaiting a read` : ""}${sources.nvd.apiKey ? "" : " (no API key — 5 reads per 30 s)"}`} />
        <SourceLine
          ok={sources.intuneSoftware.connected && !sources.intuneSoftware.error && !!sources.intuneSoftware.lastSync}
          warn={sources.intuneSoftware.connected && !!sources.intuneSoftware.error}
          text={sources.intuneSoftware.connected ? `Intune software inventory: ${sources.intuneSoftware.assetsWithSoftware} device${sources.intuneSoftware.assetsWithSoftware === 1 ? "" : "s"}, synced ${when(sources.intuneSoftware.lastSync)}` : "Intune not connected — endpoint OS builds and installed software come from it"}
          detail={sources.intuneSoftware.error}
        />
        <SourceLine
          ok={d.connected && d.enabled && !d.error && !!d.lastSync}
          warn={d.connected && d.enabled && !!d.error}
          text={!d.connected ? "Defender Vulnerability Management: connect the M365 Security connector to read Defender's per-device CVEs"
            : !d.enabled ? "Defender Vulnerability Management switched off on the M365 Security connector"
            : `Defender Vulnerability Management: ${d.machines} machine${d.machines === 1 ? "" : "s"}, ${d.assetsWithVulnerabilities} with open CVEs, synced ${when(d.lastSync)}`}
          detail={d.error}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12, color: theme.color.muted }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: canToggleRisk ? "pointer" : "default" }}>
          <input type="checkbox" checked={status.autoRiskEnabled} disabled={!canToggleRisk || busy !== null} onChange={(e) => toggleAutoRisk(e.target.checked)} />
          <span>Raise a risk register entry automatically when an advisory is confirmed on any asset, or likely on a critical / essential one (one entry per advisory, tentatively remediated once nothing is exposed)</span>
        </label>
        <PortalLink to="/risk-register" style={{ fontSize: 11, color: theme.color.accentText, textDecoration: "none", fontWeight: 600, marginLeft: "auto" }}>Open the risk register</PortalLink>
      </div>
      {notice && <div style={{ fontSize: 11, color: theme.color.muted }}>{notice}</div>}
    </div>
  );
}
